/**
 * UserRepository - Centralized user data management and persistence
 */

import type { UserData, ABTestVariant, UserAnswers } from '../types/user.types';
import earlyAbtestManager, { type EarlyAbtestManager } from '../core/early-abtest-manager.ts';

export class UserRepository {
  // Storage key
  private readonly STORAGE_KEY = 'vocalistUserData';

  // Default user data structure
  private readonly defaultData: UserData = {
    email: '',
    plan: 'annual',
    familyPlan: false,
    answers: {},
    abTestVariant: '',
    multiSelectAnswers: {
      music: [],
    },
  };

  // Current user data
  private data: UserData | null = null;

  private readonly abtestManager: EarlyAbtestManager;

  constructor(abtestManager: EarlyAbtestManager = earlyAbtestManager) {
    this.abtestManager = abtestManager;
  }

  /**
   * Initialize repository
   */
  init(): UserData {
    // Resolve the page-level assignment before reading application state. The manager is
    // idempotent, so this adopts the same decision made by the entry point without a redraw.
    const earlyAssignment = this.abtestManager.resolve();
    this.load();
    if (!this.data) {
      this.data = { ...this.defaultData };
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (this.data.abTestVariant !== earlyAssignment) {
      this.data.abTestVariant = earlyAssignment;
      this.save();
    }

    // Fills ab-test-variant in data if missing, don't remove
    this.getABTestVariant();

    // Check for paywall in URL - this determines which paywall config to use
    const paywallParam = urlParams.get('paywall');
    if (paywallParam) {
      this.data.paywall = paywallParam;
      this.save();
    } else if (this.data.abTestVariant === 'pw-v4') {
      this.data.paywall = 'paddle_3_options_v4_gur';
      this.save();
    } else if (this.data.abTestVariant === 'pw-v5') {
      this.data.paywall = 'v5';
      this.save();
    } else if (this.data.abTestVariant === 'pw-v6') {
      this.data.paywall = 'v6';
      this.save();
    }

    // Check for email in URL
    if (window.location.search.includes('email=')) {
      const parts = window.location.search.split('email=');
      const email = parts.length > 1 ? (parts[1] || '').split('&')[0] : '';
      if (this.data && email) {
        this.data.email = email;
        this.save();
      }
    }

    return this.data || this.defaultData;
  }

  /**
   * Get campaign tracking parameters from URL
   */
  getCampaignParams(): { network: string; campaign_name: string; adset_name: string; ad_name: string, campaign_id: string; adgroup_id: string; ad_id: string } {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      network: urlParams.get('network') || 'organic',
      campaign_name: urlParams.get('campaign_name') || '',
      adset_name: urlParams.get('adset_name') || '',
      ad_name: urlParams.get('ad_name') || '',
      campaign_id: urlParams.get('campaign_id') || '',
      adgroup_id: urlParams.get('adgroup_id') || '',
      ad_id: urlParams.get('ad_id') || '',
    };
  }

  /**
   * Save data to localStorage
   */
  save(): boolean {
    try {
      if (this.data) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.data));
      }
      return true;
    } catch (e) {
      console.error('Failed to save user data:', e);
      return false;
    }
  }

  /**
   * Load data from localStorage
   */
  load(): UserData {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const parsedData = JSON.parse(saved);
        this.data = { ...this.defaultData, ...parsedData };
      } else {
        this.data = { ...this.defaultData };
      }
    } catch (e) {
      console.error('Failed to load user data:', e);
      this.data = { ...this.defaultData };
    }
    return this.data || this.defaultData;
  }

  /**
   * Get specific field
   */
  get<K extends keyof UserData>(field?: K): K extends undefined ? UserData : UserData[K] {
    if (!this.data) {
      this.load();
    }
    return (field ? this.data![field] : this.data) as any;
  }

  /**
   * Set specific field
   */
  set<K extends keyof UserData>(field: K, value: UserData[K]): UserData[K] {
    if (!this.data) {
      this.load();
    }
    this.data![field] = value;
    this.save();
    return this.data![field];
  }

  /**
   * Update multiple fields
   */
  update(updates: Partial<UserData>): UserData {
    if (!this.data) {
      this.load();
    }
    this.data = { ...this.data!, ...updates };
    this.save();
    return this.data;
  }

  /**
   * Set answer for a question
   */
  setAnswer(question: string, answer: string | string[] | boolean): UserAnswers {
    if (!this.data) {
      this.load();
    }
    if (!this.data!.answers) {
      this.data!.answers = {};
    }
    this.data!.answers[question] = answer;
    this.save();
    return this.data!.answers;
  }

  /**
   * Get answer for a question
   */
  getAnswer(question: string): string | string[] | boolean | null {
    if (!this.data) {
      this.load();
    }
    const answer = this.data!.answers ? this.data!.answers[question] : undefined;
    return answer !== undefined ? answer : null;
  }

  /**
   * Set multi-select answer
   */
  setMultiSelectAnswer(question: string, values: string[]): Record<string, string[]> {
    if (!this.data) {
      this.load();
    }
    if (!this.data!.multiSelectAnswers) {
      this.data!.multiSelectAnswers = { music: [] };
    }
    this.data!.multiSelectAnswers[question] = values;
    this.save();
    return this.data!.multiSelectAnswers;
  }

  /**
   * Get email
   */
  getEmail(): string {
    return this.get('email');
  }

  /**
   * Set email
   */
  setEmail(email: string): string {
    return this.set('email', email);
  }

  getRandomVariant(items: ABTestVariant[]): ABTestVariant {
    if (items.length === 0) {
      'none';
    }
    return items[Math.floor(Math.random() * items.length)]!;
  }

  /**
   * Remove variants that are not supported by the current browser.
   * Voice detection works smoothly only in Safari.
   */
  removeUnsupportedVariants(variants: ABTestVariant[]): ABTestVariant[] {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (!isSafari) {
      return variants.filter(v => !v.startsWith('bday-'));
    }
    return variants;
  }

  /**
   * Get A/B test variant
   * Ensures variant is set and returns it
   */
  getABTestVariant(): ABTestVariant | '' {
    if (!this.data) {
      this.load();
    }

    const earlyAssignment = this.abtestManager.resolve();
    if (this.data!.abTestVariant !== earlyAssignment) {
      this.data!.abTestVariant = earlyAssignment;
      this.save();
    }
    return this.data!.abTestVariant;
  }

  /**
   * Get all data for external use (e.g., Paddle, Statsig)
   */
  getAllData(): UserData {
    if (!this.data) {
      this.load();
    }
    return { ...this.data! };
  }

  /**
   * Clear all user data
   */
  clear(): boolean {
    this.data = { ...this.defaultData };
    localStorage.removeItem(this.STORAGE_KEY);
    return true;
  }
}

// Create singleton instance
const userRepository = new UserRepository();

// Make globally available for backward compatibility
if (typeof window !== 'undefined') {
  (window as any).UserRepository = userRepository;
}

export default userRepository;
